(() => {
  if (customElements.get('coin-product-reviews')) return;

  const openReviewDialogs = new Set();

  customElements.define('coin-product-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.dialog = this.querySelector('[data-review-dialog]');
      this.form = this.querySelector('[data-review-form]');
      this.rating = this.querySelector('[data-review-rating]');
      this.list = this.querySelector('[data-review-list]');
      this.filter = this.querySelector('[data-review-filter]');
      this.sort = this.querySelector('[data-review-sort]');
      this.more = this.querySelector('[data-review-more]');
      this.empty = this.querySelector('[data-review-empty]');
      this.noMatches = this.querySelector('[data-review-no-matches]');
      this.status = this.querySelector('[data-review-status]');
      this.items = this.list ? [...this.list.querySelectorAll('[data-review-item]')] : [];
      this.itemOrder = new Map(this.items.map((item, index) => [item, index]));
      this.pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize, 10) || 5);
      this.visibleCount = this.pageSize;

      this.querySelectorAll('[data-review-open]').forEach((opener) => {
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
        opener.hidden = false;
        opener.addEventListener('click', () => this.openDialog(opener), { signal });
      });

      this.querySelectorAll('[data-review-close]').forEach((closer) => {
        closer.addEventListener('click', () => this.dialog?.close(), { signal });
      });

      this.dialog?.addEventListener('close', () => {
        this.unlockScroll();
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, { signal });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (
          event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom
        ) this.dialog.close();
      }, { signal });

      // This form is intentionally presentational: no request, persistence, or success state.
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.form.reportValidity();
      }, { signal });

      this.rating?.addEventListener('change', () => this.updateRating(), { signal });
      this.updateRating();

      [this.filter, this.sort].forEach((control) => {
        control?.addEventListener('change', () => {
          this.visibleCount = this.pageSize;
          this.updateReviews(true);
        }, { signal });
      });

      this.more?.addEventListener('click', () => {
        this.visibleCount += this.pageSize;
        this.updateReviews(true);
      }, { signal });

      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const helpful = event.target.closest('[data-review-helpful]');
        if (!helpful || helpful.closest('coin-product-reviews') !== this) return;
        const pressed = helpful.getAttribute('aria-pressed') === 'true';
        helpful.setAttribute('aria-pressed', String(!pressed));
      }, { signal });
      this.querySelectorAll('[data-review-helpful]').forEach((button) => { button.hidden = false; });

      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target instanceof Element ? event.target.closest('[data-review-item]') : null;
        const blockId = event.detail?.blockId || item?.dataset.reviewBlockId;
        let selectedItems = blockId
          ? this.items.filter((review) => review.dataset.reviewBlockId === String(blockId))
          : [];
        if (!selectedItems.length && item && !item.dataset.reviewBlockId && this.items.includes(item)) {
          selectedItems = [item];
        }
        if (!selectedItems.length) return;
        if (this.filter) this.filter.value = 'all';
        const sortedItems = this.filteredItems();
        const lastIndex = Math.max(...selectedItems.map((review) => sortedItems.indexOf(review)));
        this.visibleCount = Math.max(this.visibleCount, this.pageSize, lastIndex + 1);
        this.updateReviews(false);
      }, { signal });

      this.updateReviews(false);
      const toolbar = this.querySelector('[data-review-toolbar]');
      if (toolbar) toolbar.hidden = this.items.length === 0;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
      this.opener = null;
    }

    openDialog(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener;
      this.dialog.showModal();
      openReviewDialogs.add(this);
      document.documentElement.classList.add('coin-reviews-modal-open');
    }

    unlockScroll() {
      openReviewDialogs.delete(this);
      if (!openReviewDialogs.size) {
        document.documentElement.classList.remove('coin-reviews-modal-open');
      }
    }

    updateRating() {
      if (!this.rating) return;
      const selected = Number(this.rating.querySelector('input[name="rating"]:checked')?.value || 0);
      this.rating.querySelectorAll('input[name="rating"]').forEach((input) => {
        [...(input.labels || [])].forEach((label) => {
          label.classList.toggle('is-selected', Number(input.value) <= selected);
        });
      });
    }

    filteredItems() {
      const rating = Number(this.filter?.value || 0);
      const sort = this.sort?.value || 'recent';
      return this.items.filter((item) => !rating || Number(item.dataset.rating) === rating)
        .sort((first, second) => {
          const ratingDifference = Number(first.dataset.rating) - Number(second.dataset.rating);
          if (ratingDifference && sort === 'highest') return -ratingDifference;
          if (ratingDifference && sort === 'lowest') return ratingDifference;
          const dateDifference = (Number(second.dataset.date) || 0) - (Number(first.dataset.date) || 0);
          return dateDifference || this.itemOrder.get(first) - this.itemOrder.get(second);
        });
    }

    updateReviews(announce) {
      const filtered = this.filteredItems();
      const visible = Math.min(this.visibleCount, filtered.length);
      const fragment = document.createDocumentFragment();

      this.items.forEach((item) => { item.hidden = true; });
      filtered.forEach((item, index) => {
        item.hidden = index >= visible;
        fragment.appendChild(item);
      });
      this.list?.appendChild(fragment);

      if (this.empty) this.empty.hidden = this.items.length > 0;
      if (this.noMatches) this.noMatches.hidden = !this.items.length || filtered.length > 0;
      if (this.more) this.more.hidden = visible >= filtered.length;
      if (announce && this.status) this.status.textContent = `${visible} of ${filtered.length} reviews`;
    }
  });
})();
